/**
 * Minimal, spec-oriented Server-Sent-Events parser (https://html.spec.whatwg.org/multipage/server-sent-events.html)
 * for streams that are read with `fetch` (EventSource only supports GET).
 *
 * - Chunks may end anywhere (mid-line, mid-field, between `\r` and `\n`, mid-UTF-8 sequence when used with
 *   a streaming `TextDecoder`).
 * - Line endings `\n`, `\r\n` and `\r`; a leading BOM is skipped.
 * - Multiple `data:` lines are joined with `\n`; comment lines (`: …`) are ignored.
 * - `data` is parsed as JSON when possible, otherwise passed through as string.
 *
 * @typedef {{ event: string, data: any, id?: string }} SseEvent
 */

/**
 * @param {(event: SseEvent) => void} onEvent
 */
export function createSseParser(onEvent) {
  let buffer = '';
  let started = false;
  let eventName = '';
  /** @type {string[]} */
  let dataLines = [];
  /** @type {string|undefined} */
  let lastId;

  function dispatch() {
    if (dataLines.length === 0) {
      eventName = '';
      return;
    }
    const raw = dataLines.join('\n');
    let data = raw;
    try {
      data = JSON.parse(raw);
    } catch {
      // keep the raw string
    }
    const event = { event: eventName || 'message', data, ...(lastId !== undefined ? { id: lastId } : {}) };
    eventName = '';
    dataLines = [];
    onEvent(event);
  }

  /** @param {string} line */
  function processLine(line) {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') eventName = value;
    else if (field === 'data') dataLines.push(value);
    else if (field === 'id' && !value.includes('\0')) lastId = value;
    // `retry` and unknown fields are ignored
  }

  return {
    /** Feeds a decoded text chunk. @param {string} chunk */
    push(chunk) {
      if (!chunk) return;
      buffer += chunk;
      if (!started) {
        if (buffer.length === 0) return;
        if (buffer.charCodeAt(0) === 0xfeff) buffer = buffer.slice(1);
        started = true;
      }
      const re = /\r\n|\r|\n/g;
      let start = 0;
      let match;
      while ((match = re.exec(buffer))) {
        // a trailing lone `\r` may be the first half of `\r\n` – wait for the next chunk
        if (match[0] === '\r' && match.index === buffer.length - 1) break;
        processLine(buffer.slice(start, match.index));
        start = match.index + match[0].length;
      }
      buffer = buffer.slice(start);
    },
    /** End of stream: processes a pending line and dispatches a not yet terminated event (lenient). */
    end() {
      if (buffer.endsWith('\r')) buffer = buffer.slice(0, -1);
      if (buffer !== '') processLine(buffer);
      buffer = '';
      dispatch();
    },
  };
}

/**
 * Reads a `fetch` Response body as SSE and calls `onEvent` for every event. Resolves at the end of the stream.
 * @param {Response} response
 * @param {(event: SseEvent) => void} onEvent
 */
export async function readSseResponse(response, onEvent) {
  const parser = createSseParser(onEvent);
  if (!response.body || typeof response.body.getReader !== 'function') {
    parser.push(await response.text());
    parser.end();
    return;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(typeof value === 'string' ? value : decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode());
    parser.end();
  } finally {
    reader.releaseLock?.();
  }
}
